import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import type { ActionCreator, AnyAction } from 'redux';
import type { SetRequired } from 'type-fest';
import { DISABLE_PACKAGE_LABELS } from '../../../../shared/featureFlags/flagNames';
import { useOrganizationPackageLabels } from '../../../hooks/api/packageLabels';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { FeatureFlagRequired, FormLabel, FormLabelVariant } from '../../generic';
import FilterCard from '../../generic/Filter/FilterCard';
import type { PackageInventoryParams } from '../PackageInventoryContainer';
import useFetchPackageVersionProjects from '../hooks/useFetchPackageVersionProjects';
import PackageLabelsFilter from './PackageLabelsFilter';

type PackageFiltersForVersionProps = {
  arePackageLabelsLoading: boolean;
  packageLabelOptions: string[];
  selectedPackageLabels: string[];
  setFilters: (...args: unknown[]) => void;
};

/**
 * PackageFiltersForVersion has contains all filters related to filtering projects for a given package version.
 * It currently includes package labels only.
 */
const PackageFiltersForVersion: VoidFunctionComponent<PackageFiltersForVersionProps> = ({
  arePackageLabelsLoading,
  packageLabelOptions,
  selectedPackageLabels,
  setFilters,
}) => (
  <FeatureFlagRequired flagName={DISABLE_PACKAGE_LABELS} isNotEnabled>
    <FilterCard id="package-inventory-package-filters" title="Package">
      <FormLabel label="Labels" variant={FormLabelVariant.Filter}>
        <PackageLabelsFilter
          isLoading={arePackageLabelsLoading}
          packageLabelOptions={packageLabelOptions}
          selectedPackageLabels={selectedPackageLabels}
          setFilters={setFilters}
        />
      </FormLabel>
    </FilterCard>
  </FeatureFlagRequired>
);

type PackageVersionFiltersPackagesContainerProps = {
  /**
   * Package locator and version are required to filter projects for a given package version.
   */
    params: SetRequired<PackageInventoryParams, 'packageLocator' | 'version'>;
  };

/**
 * Stateful container for the package filters for a given package version.
 */
export const PackageVersionFiltersPackagesContainer: VoidFunctionComponent<
  PackageVersionFiltersPackagesContainerProps
> = ({ params }) => {
  // Load org package labels with the useOrganizationPackageLabels hook.
  const { data: packageLabelsResponse, isLoading: arePackageLabelsLoading } = useOrganizationPackageLabels();
  const packageLabels = packageLabelsResponse?.packageLabels ?? [];
  const packageLabelOptions = packageLabels.map((packageLabel) => packageLabel.name);

  const selectedPackageLabels = useSelector(PackageInventoryStore.selectors.getFiltersPackageLabels);

  const setFilters = useFetchPackageVersionProjects(
    PackageInventoryStore.actions.setFilters as unknown as ActionCreator<AnyAction>,
    params.packageLocator,
    params.version,
  );

  return (
    <PackageFiltersForVersion
      arePackageLabelsLoading={arePackageLabelsLoading}
      packageLabelOptions={packageLabelOptions}
      selectedPackageLabels={selectedPackageLabels}
      setFilters={setFilters}
    />
  );
};
